import { useEffect, useRef, useState } from 'react';

const ACCENT_TEXT = {
  blue: 'text-showdown-blue',
  green: 'text-showdown-green',
  yellow: 'text-showdown-yellow',
};

/**
 * A single reaction video, shown anonymously during voting (no ingredient
 * name — that's the whole point of the blind vote). Handles:
 *  - iOS Safari inline/muted autoplay requirements
 *  - lazy preload (only the active round's two videos ever load)
 *  - graceful fallback if the video file is missing/unplayable, so a school
 *    wifi hiccup (or a not-yet-uploaded clip) never looks like a broken page
 *
 * Cropped to a square centred on the plate (not the full portrait 9:16
 * frame) and width-capped, so two cards stack on one mobile screen without
 * scrolling — the "pick" affordance is an overlay rather than a separate
 * row, to keep each card's footprint to just the square itself.
 *
 * `accent` (blue/green/yellow) colours the "Reaction A/B" label so the two
 * options in a round are easy to tell apart at a glance.
 */
export default function VideoCard({ reaction, label, accent = 'blue', active, onSelect, disabled }) {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [reaction.video]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !active) return;
    el.currentTime = 0;
    const playPromise = el.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        // Autoplay can still be blocked in some contexts; the visible
        // play affordance in the poster/fallback covers that case.
      });
    }
  }, [active, reaction.video]);

  return (
    <button
      type="button"
      onClick={() => onSelect(reaction.id)}
      disabled={disabled}
      aria-label={`Pick ${label}: this reaction`}
      className="group relative mx-auto block w-[min(38vh,80vw,320px)] overflow-hidden rounded-3xl border-4 border-transparent bg-black text-left shadow-lg transition
                 focus-visible:border-showdown-yellow hover:border-showdown-pink active:scale-[0.98]
                 disabled:pointer-events-none disabled:opacity-60 sm:w-full sm:max-w-xs"
    >
      <div className="relative aspect-square w-full bg-black">
        {!failed ? (
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            src={active ? reaction.video : undefined}
            preload={active ? 'auto' : 'none'}
            poster={reaction.poster}
            muted
            playsInline
            loop
            autoPlay={active}
            aria-label={`${label} reaction video, ingredient hidden until voting is complete`}
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-showdown-pink to-showdown-pink-dark text-showdown-ink">
            <BubbleIcon className="h-10 w-10 opacity-90" />
            <span className="text-xs font-semibold uppercase tracking-wide opacity-90">Video loading…</span>
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <span
          className={`absolute left-2.5 top-2.5 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest sm:text-xs ${ACCENT_TEXT[accent]}`}
        >
          {label}
        </span>
        <span className="absolute inset-x-0 bottom-0 bg-showdown-pink/90 py-2 text-center text-sm font-extrabold uppercase tracking-wide text-showdown-ink sm:py-2.5 sm:text-base">
          Pick this one
        </span>
      </div>
    </button>
  );
}

function BubbleIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
      <circle cx="8" cy="14" r="5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="16" cy="9" r="3" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="17" cy="17" r="1.5" fill="currentColor" />
    </svg>
  );
}
